import { getCursorPositionStyles } from '@/utils/tinyMCE/getCursorPositionStyles'
import { regEx } from '@/utils/global/regEx'

const preserveCurrentNodeStyles = (editor: Editor) => {
  const selection = editor?.selection
  const selectionRange = selection?.getRng?.()
  const isCollapsed = selectionRange?.collapsed

  let capturedStyleString = ''
  let targetParagraph: HTMLElement | null = null

  if (!isCollapsed) {
    const startContainer = selectionRange?.startContainer
    const startNode =
      startContainer?.nodeType === Node.TEXT_NODE
        ? startContainer.parentElement
        : (startContainer as Element)

    targetParagraph = startNode?.closest?.('p') || null
  } else {
    const selectedNode = selection?.getNode?.()
    targetParagraph = selectedNode?.closest?.('p')
  }

  if (!targetParagraph) {
    return
  }

  const mergeAllSpanStyles = (element: HTMLElement): string => {
    const allSpans = Array.from(
      element?.querySelectorAll?.('span[style]') ?? [],
    )

    if (allSpans?.length === 0) return ''

    const spanDepths = allSpans?.map((span) => {
      let depth = 0
      let parent = span?.parentElement

      while (parent && parent !== element) {
        depth++
        parent = parent?.parentElement
      }

      return { span, depth }
    })

    spanDepths.sort((a, b) => a.depth - b.depth)

    const mergedStyles = new Map<string, string>()
    const textDecorations = new Set<string>()

    spanDepths.forEach(({ span }) => {
      const styleAttr = span?.getAttribute?.('style') || ''

      styleAttr.split(';').forEach((declaration) => {
        const trimmed = declaration.trim()
        if (!trimmed) return

        const colonIndex = trimmed.indexOf(':')
        if (colonIndex === -1) return

        const property = trimmed.substring(0, colonIndex).trim()
        const value = trimmed.substring(colonIndex + 1).trim()

        if (property && value) {
          if (property === 'text-decoration') {
            value.split(regEx.multipleWhiteSpace).forEach((decoration) => {
              const trimmedDecoration = decoration.trim()
              if (trimmedDecoration && trimmedDecoration !== 'none') {
                textDecorations.add(trimmedDecoration)
              }
            })
          } else {
            mergedStyles.set(property, value)
          }
        }
      })
    })

    if (textDecorations.size > 0) {
      mergedStyles.set('text-decoration', Array.from(textDecorations).join(' '))
    }

    const styleString = Array.from(mergedStyles.entries())
      .map(([prop, val]) => `${prop}: ${val}`)
      .join('; ')

    return styleString
  }

  const hasSpans =
    (targetParagraph?.querySelectorAll?.('span[style]')?.length ?? 0) > 0

  if (hasSpans) {
    capturedStyleString = mergeAllSpanStyles(targetParagraph)
  } else {
    capturedStyleString = getCursorPositionStyles(editor)
  }

  if (!capturedStyleString) {
    return
  }

  setTimeout(() => {
    const currentNode = editor?.selection?.getNode?.()
    const currentParagraph = currentNode?.closest?.('p')

    if (!currentParagraph) {
      return
    }

    const isTextBoxEmpty =
      (currentParagraph.textContent?.trim?.()?.length ?? 0) === 0

    if (!isTextBoxEmpty) {
      return
    }

    const hasLostSpanWrapper = !currentParagraph?.querySelector?.('span[style]')

    if (hasLostSpanWrapper) {
      const br = currentParagraph?.querySelector?.('br')

      if (br) {
        const span = editor.dom.create('span', {
          style: capturedStyleString,
          'data-mce-style': capturedStyleString,
        })

        // Add zero-width space before br to prevent TinyMCE from moving br outside span
        span.innerHTML = '\u200B'
        br.parentNode?.insertBefore(span, br)
        span.appendChild(br)

        // Notify TinyMCE that the DOM changed so it updates the toolbar
        editor.nodeChanged()
      }
    }
  }, 0)
}

export const persistStylesOnBackspace = ({
  editor,
  plainTextMode,
}: {
  editor: Editor
  plainTextMode: boolean
}) => {
  if (plainTextMode) return

  editor.on('keydown', (e: KeyboardEvent) => {
    const userPressedBackspaceOrDelete =
      e.key === 'Backspace' || e.key === 'Delete'
    if (userPressedBackspaceOrDelete) {
      preserveCurrentNodeStyles(editor)
    }
  })
}

export default persistStylesOnBackspace
